import {useRef, type ReactNode} from 'react'
import type {ClientDiffLine} from '../../types'
import type {PrunedIconButtonProps} from '../ExpandableHunkHeaderDiffLine'
import {isEmptyDiffLine} from '../../helpers/line-helpers'
import {Cell} from './Cell'
import {HunkKebabIcon} from './HunkKebabIcon'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {useGridCellButtonProps} from '../../hooks/use-grid-cell-button-props'

/**
 * Renders a hunk header cell
 */
export function HunkCell({
  diffAnchor,
  diffLine,
  isLeftSide,
  isSplit,
  renderHunkButton,
}: {
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  isLeftSide: boolean
  isSplit: boolean
  renderHunkButton?: (additionalProps: PrunedIconButtonProps) => ReactNode | null
}) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [cellProps, buttonProps] = useGridCellButtonProps(cellRef)

  if (isEmptyDiffLine(diffLine)) return null

  const key = `HunkCell-left:${diffLine.left}-right:${diffLine.right}`

  return (
    <Cell
      className="diff-hunk-cell"
      colSpan={4}
      columnIndex={0}
      diffAnchor={diffAnchor}
      diffLine={diffLine}
      isLeftSide={isLeftSide}
      isSplit={isSplit}
      key={key}
      ref={cellRef}
      style={{backgroundColor: 'var(--bgColor-accent-muted, var(--color-accent-subtle))', flexGrow: 1}}
      {...cellProps}
    >
      <div className="d-flex flex-row">
        {renderHunkButton?.(buttonProps) ?? <HunkKebabIcon isSplit={isSplit} />}
        <code className="diff-text-cell hunk">
          {/* Explicitly mark html as safe because it is server-sanitized */}
          <VerifiedHTMLDiv
            className="diff-text-inner color-fg-muted"
            // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
            html={diffLine.html as SafeHTMLString}
          />
        </code>
      </div>
    </Cell>
  )
}

try{ HunkCell.displayName ||= 'HunkCell' } catch {}